import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';
import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSCard } from '@/components/shared/card/Card';
import { Search } from '@/components/shared/Search';
import { Loading } from '@/components/shared/Loading';
import { ReadTemplate, useGetTemplatesForLocationQuery } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { useMyLocation } from '@/services/useLocations';
import { TemplateList, TemplateListItem } from './TemplateList';

const itemsPerPage = 10;

const formatTargetType = (template: ReadTemplate) => {
  return template.target?.type || 'Unknown';
};

export const TemplatePage: FC = () => {
  const navigate = useNavigate();
  const location = useMyLocation();
  const [allTemplates, setAllTemplates] = useState<TemplateListItem[]>([]);
  const [currentTemplates, setCurrentTemplates] = useState<TemplateListItem[]>([]);
  const [forcePage, setForcePage] = useState<number | undefined>(undefined);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);

  const breadcrumbs: NavPage[] = useMemo(
    () => [
      {
        name: strings.daily.templates.listTitle,
        to: '/templates'
      }
    ],
    []
  );

  const [{ data, fetching }] = useGetTemplatesForLocationQuery({
    variables: { locationId: location.id },
    requestPolicy: 'network-only'
  });

  const updatePageCount = useCallback(
    (items: TemplateListItem[], fromSearch = false) => {
      let offset = itemOffset;

      if (fromSearch) {
        offset = 0;
        setItemOffset(0);
      }

      const endOffset = offset + itemsPerPage;
      setCurrentTemplates(items.slice(offset, endOffset));
      setPageCount(Math.ceil(items.length / itemsPerPage));
    },
    [itemOffset]
  );

  useEffect(() => {
    const templates =
      (data?.templatesForLocation?.filter(Boolean) as ReadTemplate[] | undefined)?.map(template => ({
        id: template.id || '',
        name: template.name,
        targetType: formatTargetType(template),
        color: template.properties ? JSON.parse(template.properties).color : undefined,
        shiftCount: template.stations?.reduce((prevTot, station) => prevTot + (station.positions?.length || 0), 0) || 0
      })) || [];

    templates.sort((left, right) => {
      const byName = left.name.localeCompare(right.name);
      if (byName !== 0) {
        return byName;
      }

      return left.targetType.localeCompare(right.targetType);
    });

    setAllTemplates(templates);
    updatePageCount(templates);
  }, [data?.templatesForLocation, updatePageCount]);

  const onSearch = (value: string) => {
    setForcePage(0);
    const searchTerm = value.toLowerCase();
    updatePageCount(
      allTemplates.filter(
        template =>
          template.name.toLowerCase().includes(searchTerm) || template.targetType.toLowerCase().includes(searchTerm)
      ),
      true
    );
  };

  const handlePageClick = (event: { selected: number }) => {
    setForcePage(undefined);
    const newOffset = (event.selected * itemsPerPage) % allTemplates.length;
    setItemOffset(newOffset);
  };

  useEffect(() => {
    updatePageCount(allTemplates);
  }, [allTemplates, itemOffset, updatePageCount]);

  if (fetching && allTemplates.length === 0) {
    return <Loading />;
  }

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.daily.templates.listTitle} breadcrumbs={breadcrumbs} icon={<FiCalendar />} />}
    >
      <PageSizer className="flex gap-5 flex-col" mode={SizerMode.PAGE}>
        <div className="flex flex-row">
          <Search onChange={onSearch} />
        </div>
        {currentTemplates.length > 0 ? (
          <>
            <AnimatePresence>
              <TemplateList
                templates={currentTemplates}
                onSelectTemplate={templateId => navigate(`/templates/${templateId}`)}
              />
            </AnimatePresence>
            <ReactPaginate
              onPageChange={handlePageClick}
              previousLabel={<HiOutlineChevronLeft />}
              nextLabel={<HiOutlineChevronRight />}
              pageCount={pageCount}
              pageRangeDisplayed={1}
              // @ts-ignore
              renderOnZeroPageCount={null}
              pageLabelBuilder={page => strings.common.pagination.pageXOfY(page, pageCount)}
              containerClassName="flex items-center space-x-2 font-semibold justify-center"
              pageClassName="hidden"
              activeClassName="!block"
              breakLabel=""
              forcePage={forcePage}
            />
          </>
        ) : (
          <TSCard className="font-semibold flex justify-center pl-6 pr-3 py-6">
            {strings.daily.templates.noTemplates}
          </TSCard>
        )}
      </PageSizer>
    </PageContainer>
  );
};
